import React, { useCallback, useState } from 'react';

import { Select } from '@aline/common';
import { Button, IconButton, Typography } from '@mui/material';
import { Check, ChevronLeft, ChevronRight, Sparkles, X } from 'lucide-react';
import { AlineTooltip, ConfirmationDialog } from 'src/components';
import { ConfirmationDialogVariant } from 'src/components/generic/ConfirmationDialog';
import { Apollo } from 'src/gql/generated';
import { makeStyles } from 'tss-react/mui';

import { SUGGESTIONS_BAR_COLORS } from './suggestion-tones';
import { useSuggestionReview } from './SuggestionReviewContext';

const useStyles = makeStyles()(theme => ({
  /**
   * Plain MUI text buttons rather than AlineButton: its variants pin the brand
   * colours, and these two are the one place the decision colours have to win.
   * The decision colours are the editor suggestions banner's; the hover
   * surface is the bar's own, stated with its background in the tones.
   */
  acceptAll: {
    color: theme.appColors.acceptSuggestion,
  },
  actions: {
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing(0.75),
  },
  /**
   * A band across the whole page, flush with the top edge, styled like the
   * editor's suggestions banner. Raised so its shadow paints over the columns.
   */
  bar: {
    alignItems: 'center',
    backgroundColor: SUGGESTIONS_BAR_COLORS.background,
    borderBottom: `1px solid ${theme.palette.divider}`,
    boxShadow: '0px 0px 10px 0px rgba(0, 0, 0, 0.1)',
    display: 'flex',
    flexShrink: 0,
    gap: theme.spacing(2),
    justifyContent: 'space-between',
    padding: theme.spacing(1, 1, 1, 2),
    position: 'relative',
    zIndex: 1,
  },
  bulkButton: {
    '& .MuiButton-startIcon': {
      marginLeft: 0,
      marginRight: theme.spacing(0.75),
    },
    '&:hover': {
      backgroundColor: SUGGESTIONS_BAR_COLORS.hover,
    },
    padding: theme.spacing(0.5, 1),
    textTransform: 'none',
  },
  count: {
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing(1.5),
  },
  icon: {
    color: SUGGESTIONS_BAR_COLORS.icon,
    flexShrink: 0,
  },
  rejectAll: {
    color: theme.appColors.rejectSuggestion,
  },
  stepper: {
    alignItems: 'center',
    display: 'flex',
    fontVariantNumeric: 'tabular-nums',
    gap: theme.spacing(0.5),
  },
  stepperButton: {
    '&:hover': {
      backgroundColor: SUGGESTIONS_BAR_COLORS.hover,
    },
    color: 'inherit',
  },
}));

type BulkDecision = Apollo.PlaybookSuggestedEditDecision | null;

/**
 * A band across the top of the page. Renders only while the playbook has
 * unhandled suggestions; resolving the last one removes it. Left: the count.
 * Right: the stepper and the bulk decisions.
 * The count counts stops, so a change carrying a patch and a move is two.
 */
export const SuggestionsBar = React.memo(function SuggestionsBar({
  playbookId,
}: {
  playbookId: string;
}): JSX.Element | null {
  const { classes, cx } = useStyles();
  const { currentIndex, goToNext, goToPrevious, loading, resolveScope, total } =
    useSuggestionReview();
  const [pendingBulk, setPendingBulk] = useState<BulkDecision>(null);

  const confirmBulk = useCallback(async () => {
    if (!pendingBulk) return;
    setPendingBulk(null);
    await resolveScope({ playbookId }, pendingBulk);
  }, [pendingBulk, playbookId, resolveScope]);

  const askRejectAll = useCallback(
    () => setPendingBulk(Apollo.PlaybookSuggestedEditDecision.Reject),
    []
  );
  const askAcceptAll = useCallback(
    () => setPendingBulk(Apollo.PlaybookSuggestedEditDecision.Accept),
    []
  );
  const cancelBulk = useCallback(() => setPendingBulk(null), []);

  if (total === 0) return null;
  const isAcceptAll = pendingBulk === Apollo.PlaybookSuggestedEditDecision.Accept;

  return (
    <div className={classes.bar} data-cy={Select.playbooks.suggestionsBar}>
      <Typography className={classes.count} variant="subtitle2">
        <Sparkles className={classes.icon} size={22} strokeWidth={1.5} />
        {total} Unhandled {total === 1 ? 'Suggestion' : 'Suggestions'}
      </Typography>
      <div className={classes.actions}>
        <div className={classes.stepper}>
          <AlineTooltip arrow placement="top" title="Previous suggestion">
            <IconButton
              className={classes.stepperButton}
              data-cy={Select.playbooks.suggestionPrevious}
              size="small"
              onClick={goToPrevious}
            >
              <ChevronLeft size={20} />
            </IconButton>
          </AlineTooltip>
          {/* Zero while no suggestion is current: on load, and after a click on a rule. */}
          <Typography noWrap>
            {currentIndex === null ? 0 : currentIndex + 1} of {total}
          </Typography>
          <AlineTooltip arrow placement="top" title="Next suggestion">
            <IconButton
              className={classes.stepperButton}
              data-cy={Select.playbooks.suggestionNext}
              size="small"
              onClick={goToNext}
            >
              <ChevronRight size={20} />
            </IconButton>
          </AlineTooltip>
        </div>
        <Button
          className={cx(classes.bulkButton, classes.rejectAll)}
          data-cy={Select.playbooks.suggestionRejectAll}
          disabled={loading}
          startIcon={<X size={20} />}
          variant="text"
          onClick={askRejectAll}
        >
          Reject All
        </Button>
        <Button
          className={cx(classes.bulkButton, classes.acceptAll)}
          data-cy={Select.playbooks.suggestionAcceptAll}
          disabled={loading}
          startIcon={<Check size={20} />}
          variant="text"
          onClick={askAcceptAll}
        >
          Accept All
        </Button>
      </div>
      <ConfirmationDialog
        message={
          isAcceptAll
            ? `Accept all ${total} suggestions? Suggested additions become part of the playbook, suggested removals are deleted, and suggested edits are applied. Anything that cannot be applied yet stays pending.`
            : `Reject all ${total} suggestions? Suggested additions are removed and every other suggestion is discarded. This cannot be undone.`
        }
        open={pendingBulk !== null}
        primaryText={isAcceptAll ? 'Accept All' : 'Reject All'}
        title={isAcceptAll ? 'Accept all suggestions?' : 'Reject all suggestions?'}
        variant={isAcceptAll ? ConfirmationDialogVariant.Info : ConfirmationDialogVariant.Error}
        onClickPrimary={confirmBulk}
        onClose={cancelBulk}
      />
    </div>
  );
});
